// Small pure helpers for rendering/sorting listing cards. All computed values
// (e.g. $/sqft) are derived only from the listing's own fields — never invented.

import type { ListingDetail } from "../../lib/jlApi";

// Parse a possibly-formatted numeric string ("1,850", "$465,000") to a number.
function parseNum(v: string | number | undefined | null): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(String(v).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Price per square foot, only when both price and sqft are valid.
export function pricePerSqFt(listing: ListingDetail): number | null {
  const price = parseNum(listing.price) ?? parseNum(listing.priceDisplay);
  const sqft = parseNum(listing.sqFtTotal);
  if (!price || !sqft) return null;
  return Math.round(price / sqft);
}

// Extract "days on market" from daysListedText like "Listed 5 days ago".
// Returns 0 for today/hours-ago phrasing, or null if unparseable.
export function parseDaysAgo(text: string): number | null {
  if (!text) return null;
  const t = text.toLowerCase();
  if (t.includes("today") || t.includes("hour") || t.includes("just")) return 0;
  const m = t.match(/(\d+)\s*day/);
  if (m) return Number(m[1]);
  const w = t.match(/(\d+)\s*week/);
  if (w) return Number(w[1]) * 7;
  const mo = t.match(/(\d+)\s*month/);
  if (mo) return Number(mo[1]) * 30;
  return null;
}

// "Just Listed" when on market <= 7 days (parsed from daysListedText).
export function isJustListed(listing: ListingDetail): boolean {
  const d = parseDaysAgo(listing.daysListedText);
  return d !== null && d <= 7;
}

// --- Compare tray (Batch 6) -------------------------------------------------
// Max homes that can be compared side-by-side at once.
export const MAX_COMPARE = 3;

// A listing captured for comparison, plus its map coordinates (looked up from
// the live markers when added) so we can compute straight-line beach distance.
export interface CompareEntry {
  listing: ListingDetail;
  lat?: number;
  lng?: number;
}

// --- Distance-to-beach (Batch 6 compare tray) -------------------------------
// A small hardcoded set of public beach-access points along the Delaware
// beaches. Distances are computed as straight-line ("as the crow flies") and
// labeled as such in the UI — never presented as drive time.
export const BEACHES: { name: string; lat: number; lng: number }[] = [
  { name: "Lewes Beach", lat: 38.7834, lng: -75.1391 },
  { name: "Rehoboth Beach", lat: 38.7168, lng: -75.0757 },
  { name: "Dewey Beach", lat: 38.6871, lng: -75.0757 },
  { name: "Bethany Beach", lat: 38.5396, lng: -75.0554 },
  { name: "Fenwick Island", lat: 38.4610, lng: -75.0490 },
];

// Great-circle distance in miles between two lat/lng points (Haversine).
export function haversineMiles(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 3958.8; // Earth radius in miles
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Nearest beach + straight-line distance for a listing's coordinates.
// Returns null when coordinates are missing/invalid.
export function nearestBeach(
  lat: number | undefined,
  lng: number | undefined
): { name: string; miles: number } | null {
  if (
    lat === undefined ||
    lng === undefined ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lng)
  )
    return null;
  let best: { name: string; miles: number } | null = null;
  for (const b of BEACHES) {
    const miles = haversineMiles(lat, lng, b.lat, b.lng);
    if (!best || miles < best.miles) best = { name: b.name, miles };
  }
  return best;
}

export type SortKey = "newest" | "price-asc" | "price-desc";

export const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "newest", label: "Newest" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
];

export function sortListings(
  listings: ListingDetail[],
  sort: SortKey
): ListingDetail[] {
  const arr = [...listings];
  if (sort === "price-asc") {
    arr.sort((a, b) => (a.price || 0) - (b.price || 0));
  } else if (sort === "price-desc") {
    arr.sort((a, b) => (b.price || 0) - (a.price || 0));
  } else {
    // newest: fewer days on market first; unknown sinks to the bottom.
    arr.sort((a, b) => {
      const da = parseDaysAgo(a.daysListedText);
      const db = parseDaysAgo(b.daysListedText);
      if (da === null && db === null) return 0;
      if (da === null) return 1;
      if (db === null) return -1;
      return da - db;
    });
  }
  return arr;
}
